import { ActionIcon, Collapse } from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';
import { ReactNode, useState, useCallback } from 'react';
import { Section } from './Section';

type InfoCardProps = {
  title: string;
  renderList: () => ReactNode;
  renderAddForm: (onClose: () => void) => ReactNode;
  addButtonAriaLabel?: string;
  renderAddButton?: (props: { showAddForm: boolean; onToggle: () => void; onClose: () => void }) => ReactNode;
  showAddForm?: boolean;
  onShowAddFormChange?: (show: boolean) => void;
  collapsible?: boolean;
  defaultOpen?: boolean;
};

export function InfoCard({
  title,
  renderList,
  renderAddForm,
  addButtonAriaLabel,
  renderAddButton,
  showAddForm: externalShowAddForm,
  onShowAddFormChange,
  collapsible = true,
  defaultOpen = true,
}: InfoCardProps) {
  const [internalShowAddForm, setInternalShowAddForm] = useState(false);
  const showAddForm = externalShowAddForm !== undefined ? externalShowAddForm : internalShowAddForm;

  const handleCloseAddForm = useCallback(() => {
    onShowAddFormChange?.(false);
    setInternalShowAddForm(false);
  }, [onShowAddFormChange]);

  const handleToggleAddForm = useCallback(() => {
    onShowAddFormChange?.(!showAddForm);
    setInternalShowAddForm((prev) => !prev);
  }, [onShowAddFormChange, showAddForm]);

  const addButton = renderAddButton ? (
    renderAddButton({ showAddForm, onToggle: handleToggleAddForm, onClose: handleCloseAddForm })
  ) : (
    <ActionIcon
      variant="light"
      size="xs"
      onClick={handleToggleAddForm}
      aria-label={addButtonAriaLabel || `Add ${title.toLowerCase()}`}
    >
      {showAddForm ? <IconX size={16} /> : <IconPlus size={16} />}
    </ActionIcon>
  );

  return (
    <Section title={title} collapsible={collapsible} defaultOpen={defaultOpen} headerAction={addButton}>
      <Collapse in={showAddForm}>{renderAddForm(handleCloseAddForm)}</Collapse>
      {renderList()}
    </Section>
  );
}
